import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useAppContext } from '../../contexts/AppContext';
import { useAppStore } from '../../store/appStore';
import { useGlobalSearchStore } from '../../store/globalSearchStore';
import { useWorkspaceAssistantStore } from '../../store/workspaceAssistantStore';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileBrowser } from '../../hooks/useIsMobileBrowser';
import { InsetModal } from '../InsetModal';
import { WorkspaceAssistant } from '../WorkspaceAssistant';
import { GlobalSearch } from './GlobalSearch';

const isWeb = Platform.OS === 'web';
const typing = (el: EventTarget | null) => {
  const node = el as HTMLElement | null;
  return !!node && (/^(input|textarea|select)$/i.test(node.tagName) || node.isContentEditable);
};

/** The one search palette of the app, with its shortcuts: ⌘K / Ctrl+K anywhere, "/" outside text fields, ⌘J / Ctrl+J for the assistant. */
export const GlobalSearchHost: React.FC = () => {
  const { navigateTo } = useAppContext();
  const open = useGlobalSearchStore((s) => s.open);
  const hide = useGlobalSearchStore((s) => s.hide);
  const businessId = useAppStore((s) => s.currentBusiness?._id);
  const businessRole = useAppStore((s) => s.businessRole);
  const { hasPermissionAtLeast } = usePermissions();
  const isMobileBrowser = useIsMobileBrowser();
  const canAsk = !!businessId && (businessRole === 'owner' || hasPermissionAtLeast('ai.useAssistant', 'edit'));
  const [assistantOpen, setAssistantOpen] = useState(false);

  useEffect(() => { hide(); setAssistantOpen(false); }, [businessId, hide]);

  useEffect(() => {
    if (!isWeb || !businessId) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'j' && askRef.current) {
        e.preventDefault();
        useGlobalSearchStore.getState().hide();
        askRef.current('');
      } else if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        useGlobalSearchStore.getState().toggle();
      } else if (e.key === '/' && !e.metaKey && !e.ctrlKey && !useGlobalSearchStore.getState().open && !typing(e.target)) {
        e.preventDefault();
        useGlobalSearchStore.getState().show();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [businessId]);

  // Empty = just open the assistant (the header no longer has its own button).
  const ask = useCallback((question: string) => {
    if (question) useGlobalSearchStore.getState().setAssistantSeed(question);
    if (isWeb && !isMobileBrowser) {
      useWorkspaceAssistantStore.getState().setAssistantTab(true);
      useAppStore.getState().setRightPaneCollapsed(false);
    } else setAssistantOpen(true);
  }, [isMobileBrowser]);

  const askRef = useRef<((q: string) => void) | null>(null);
  askRef.current = canAsk ? ask : null;

  if (!businessId) return null;
  return (
    <>
      {open && (
        <GlobalSearch
          visible
          onClose={hide}
          onNavigate={(nav) => navigateTo(nav.screen, nav.param, nav.routeParams)}
          onAsk={canAsk ? ask : undefined}
        />
      )}
      {assistantOpen && (
        <InsetModal visible animationType="slide" onRequestClose={() => setAssistantOpen(false)}>
          <WorkspaceAssistant onClose={() => setAssistantOpen(false)} />
        </InsetModal>
      )}
    </>
  );
};
